import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme, Container, Box, CssBaseline } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import TextReader from './components/TextReader';
import HomeDashboard from './components/HomeDashboard';
import UsageDashboard from './components/UsageDashboard';
import SavedTranslations from './components/SavedTranslations';
import Flashcards from './components/Flashcards';
import DocumentLibrary from './components/library/DocumentLibrary';
import EnhancedAdminDashboard from './components/admin/EnhancedAdminDashboard';
import AuthPage from './components/auth/AuthPage';
import UserProfile from './components/auth/UserProfile';
import ResetPassword from './components/auth/ResetPassword';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import Support from './components/legal/Support';
import TermsOfService from './components/legal/TermsOfService';
import FlashcardReview from './components/flashcards/FlashcardReview';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import { AuthProvider } from './contexts/AuthContext';
import GiftNotification from './components/GiftNotification';
import './App.css';

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    // Check if user has a preference saved in localStorage
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true' ? true : false;
  });

  useEffect(() => {
    // Apply the theme to the document
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    // Save user preference to localStorage
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: '#667eea',
        dark: '#5a67d8',
        light: '#9f7aea',
      },
      background: {
        default: darkMode ? '#121212' : '#F5F5F7',
        paper: darkMode ? '#1E1E1E' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#E4E6EB' : '#2C3E50',
        secondary: darkMode ? '#A0AEC0' : '#5A6A7A',
      },
    },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    },
    components: {
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#1E1E1E' : '#ffffff',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
            },
          },
          outlinedPrimary: {
            borderColor: '#764ba2',
            color: '#764ba2',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#6b46c1',
              backgroundColor: 'rgba(118, 75, 162, 0.08)',
            },
          },
          textPrimary: {
            color: '#764ba2',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(118, 75, 162, 0.08)',
            },
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <div className="App">
            <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
            <GiftNotification />
            
            <Container maxWidth={false} sx={{ maxWidth: '1760px', flexGrow: 1, width: '100%' }}>
              <Box sx={{ mt: 4 }}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<HomeDashboard />} />
                  <Route path="/reader" element={<TextReader />} />
                  <Route path="/login" element={<AuthPage />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/usage" element={<UsageDashboard />} />
                  <Route path="/saved-translations" element={<SavedTranslations />} />
                  <Route path="/library" element={<DocumentLibrary />} />
                  
                  {/* Protected routes */}
                  <Route path="/flashcards" element={
                    <ProtectedRoute>
                      <Flashcards />
                    </ProtectedRoute>
                  } />

                  <Route path="/flashcards/review" element={
                    <ProtectedRoute>
                      <FlashcardReview />
                    </ProtectedRoute>
                  } />

                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/admin" element={
                    <ProtectedRoute adminOnly={true}>
                      <EnhancedAdminDashboard />
                    </ProtectedRoute>
                  } />
                  
                  {/* Fallback route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Box>
            </Container>

            <Footer />

            <Analytics />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
